import { useState, useEffect } from 'react';

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setShowBanner(true);
    } else {
      const saved = JSON.parse(consent);
      setPreferences(saved);
    }
  }, []);

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    setPreferences(allAccepted);
    setShowBanner(false);
    setShowSettings(false);
  };

  const acceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    localStorage.setItem('cookieConsent', JSON.stringify(necessaryOnly));
    setPreferences(necessaryOnly);
    setShowBanner(false);
    setShowSettings(false);
  };

  const savePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    setShowBanner(false);
    setShowSettings(false);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t-2 border-gray-200 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          {!showSettings ? (
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-purple-100 rounded-lg flex-shrink-0">
                    <i className="ri-cookie-line text-purple-600 text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1 sm:mb-2">
                      🍪 We Value Your Privacy
                    </h3>
                    <p className="text-gray-700 text-xs sm:text-sm leading-relaxed">
                      We use cookies to enhance your experience, analyze site traffic, and personalize content. 
                      By clicking "Accept All", you consent to our use of cookies. You can customize your preferences or accept only necessary cookies.
                      {' '}
                      <a href="/privacy" className="text-purple-600 hover:text-purple-700 font-semibold underline">
                        Learn more
                      </a>
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2 sm:gap-3 w-full md:w-auto">
                <button
                  onClick={() => setShowSettings(true)}
                  className="px-4 sm:px-6 py-2 sm:py-2.5 border-2 border-gray-300 text-gray-700 text-xs sm:text-sm rounded-full font-semibold hover:bg-gray-50 transition-all whitespace-nowrap"
                >
                  Customize
                </button>
                <button
                  onClick={acceptNecessary}
                  className="px-4 sm:px-6 py-2 sm:py-2.5 border-2 border-purple-600 text-purple-600 text-xs sm:text-sm rounded-full font-semibold hover:bg-purple-50 transition-all whitespace-nowrap"
                >
                  Necessary Only
                </button>
                <button
                  onClick={acceptAll}
                  className="px-4 sm:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs sm:text-sm rounded-full font-semibold hover:shadow-lg hover:scale-105 transition-all whitespace-nowrap"
                >
                  Accept All
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 sm:space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">Cookie Preferences</h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-all"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              <div className="space-y-3 sm:space-y-4 max-h-[50vh] overflow-y-auto pr-2">
                {/* Necessary Cookies */}
                <div className="p-3 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl border border-gray-200">
                  <div className="flex items-start justify-between gap-3 sm:gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 sm:mb-2">
                        <i className="ri-shield-check-line text-purple-600 text-base sm:text-lg"></i>
                        <h4 className="font-bold text-gray-900 text-sm sm:text-base">Necessary Cookies</h4>
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
                          Always Active
                        </span>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-600">
                        Essential for the website to function properly. These cookies enable core functionality such as security, 
                        network management, and accessibility. They cannot be disabled.
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <div className="w-10 h-5 sm:w-12 sm:h-6 bg-purple-600 rounded-full flex items-center justify-end px-1 cursor-not-allowed opacity-50">
                        <div className="w-3 h-3 sm:w-4 sm:h-4 bg-white rounded-full"></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="p-3 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl border border-gray-200">
                  <div className="flex items-start justify-between gap-3 sm:gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 sm:mb-2">
                        <i className="ri-bar-chart-line text-purple-600 text-base sm:text-lg"></i>
                        <h4 className="font-bold text-gray-900 text-sm sm:text-base">Analytics Cookies</h4>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-600">
                        Help us understand how visitors interact with our website by collecting and reporting information anonymously. 
                        This helps us improve our service and user experience.
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <button
                        onClick={() => setPreferences({ ...preferences, analytics: !preferences.analytics })}
                        className={`w-10 h-5 sm:w-12 sm:h-6 rounded-full flex items-center transition-all ${
                          preferences.analytics 
                            ? 'bg-purple-600 justify-end' 
                            : 'bg-gray-300 justify-start'
                        } px-1 cursor-pointer`}
                      >
                        <div className="w-3 h-3 sm:w-4 sm:h-4 bg-white rounded-full"></div>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div className="p-3 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl border border-gray-200">
                  <div className="flex items-start justify-between gap-3 sm:gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 sm:mb-2">
                        <i className="ri-megaphone-line text-purple-600 text-base sm:text-lg"></i>
                        <h4 className="font-bold text-gray-900 text-sm sm:text-base">Marketing Cookies</h4>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-600">
                        Used to track visitors across websites to display relevant advertisements. 
                        These cookies help us show you personalized content and measure the effectiveness of our campaigns.
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <button
                        onClick={() => setPreferences({ ...preferences, marketing: !preferences.marketing })}
                        className={`w-10 h-5 sm:w-12 sm:h-6 rounded-full flex items-center transition-all ${
                          preferences.marketing 
                            ? 'bg-purple-600 justify-end' 
                            : 'bg-gray-300 justify-start'
                        } px-1 cursor-pointer`}
                      >
                        <div className="w-3 h-3 sm:w-4 sm:h-4 bg-white rounded-full"></div>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 sm:gap-3 pt-3 sm:pt-4 border-t border-gray-200">
                <button
                  onClick={acceptNecessary}
                  className="px-4 sm:px-6 py-2 sm:py-2.5 border-2 border-gray-300 text-gray-700 text-xs sm:text-sm rounded-full font-semibold hover:bg-gray-50 transition-all whitespace-nowrap"
                >
                  Reject All
                </button>
                <button
                  onClick={savePreferences}
                  className="flex-1 px-4 sm:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs sm:text-sm rounded-full font-semibold hover:shadow-lg hover:scale-105 transition-all whitespace-nowrap"
                >
                  Save Preferences
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Backdrop */}
      {showBanner && (
        <div className="fixed inset-0 bg-black/20 z-40 backdrop-blur-sm"></div>
      )}
    </>
  );
}
